import {
  DESTINATION_LABELS,
  DESTINATION_PEOPLE,
  type DestinationPerson,
  type TriageDestination,
} from '@/shared/triage/vocabulary';
import { cn } from '@/shared/utils/cn';

/**
 * A destination on screen: a coloured dot for the person it reaches, then the name.
 *
 * The dot is decoration (`aria-hidden`): the name is always written next to it, so the
 * colour helps the eye find «what is mine» without being the only thing that says it.
 * «Paulo y administración» is one dot split in two halves, so every name starts at the
 * same place in a column; «Se queda» keeps an empty slot of the same width for the same
 * reason. The literal class names are here and not built from the key because Tailwind
 * only generates the classes it can read in the source.
 */
const DOT: Record<DestinationPerson, string> = {
  paulo: 'bg-dest-paulo',
  maria: 'bg-dest-maria',
  administracion: 'bg-dest-admin',
};

function isDestination(key: string): key is TriageDestination {
  return Object.hasOwn(DESTINATION_LABELS, key);
}

export function DestinationDot({ destination }: { destination: TriageDestination }) {
  const people = DESTINATION_PEOPLE[destination];
  return (
    <span
      aria-hidden="true"
      data-destination-dot={destination}
      className="inline-flex size-2 shrink-0 overflow-hidden rounded-full"
    >
      {people.map((person) => (
        <span key={person} data-person={person} className={cn('h-full flex-1', DOT[person])} />
      ))}
    </span>
  );
}

/**
 * The name with its dot. Takes a plain string because the statistics endpoints key their
 * rows by string: a key the vocabulary does not know is printed as it came, without a dot.
 */
export function DestinationName({
  destination,
  className,
}: {
  destination: string;
  className?: string;
}) {
  if (!isDestination(destination)) return <span className={className}>{destination}</span>;
  return (
    <span className={cn('inline-flex items-center gap-1.5', className)}>
      <DestinationDot destination={destination} />
      {DESTINATION_LABELS[destination]}
    </span>
  );
}
